import React, { useCallback, useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import CASCOCustomQuotationModalComponent from './CASCOCustomQuotationModalComponent';
import { INSURERS_QUERY } from 'features/insuranceDocument/edit/general/insuranceData/queries';
import { INSURANCE_CONVENTIONS, INSURANCE_RISKS_QUERY, CASCO_QUOTATION } from '../../queries';
import { INSURANCE_DATA_QUERY } from 'features/insuranceDocument/queries/insuranceDocumentQueries';
import { SAVE_QUOTATION } from '../../mutations';
import { CASCO_CUSTOM_QUOTATION_SAVED } from '../../subscriptions';
import { useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { get, set, sequence } from '@totalsoft/change-tracking-react';
import { shape, greaterThan, required, stopOnFirstFailure } from '@totalsoft/pure-validations';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { isDirty } from '@totalsoft/change-tracking';
import { useDictionaryHandling, useSimpleError, useQueryWithSimpleErrorHandling } from 'hooks';
import { useMutation, useConditionalSubscription } from 'features/common/security/hooks';
import { customQuotationModalOpenVar, lastModifiedQuotationVar } from '../../variables';

const customQuotationValidator = shape({
  insurerId: required,
  conventionId: required,
  period: [required, greaterThan(0)] |> stopOnFirstFailure,
  periodicity: [required, greaterThan(0)] |> stopOnFirstFailure,
  insuredAmount: [required, greaterThan(0)] |> stopOnFirstFailure,
  insuranceAmount: [required, greaterThan(0)] |> stopOnFirstFailure
});

const initialCascoCustomData = { risks: [] };

const CASCOCustomQuotationContainer = ({ quotationId, documentId, insuranceTypeId }) => {
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const showError = useSimpleError();
  const [validation, validate] = useDirtyFieldValidation(customQuotationValidator);
  const [cascoCustomDataLens, cascoCustomDirtyInfo, resetCascoCustomDataLens] = useChangeTrackingLens(initialCascoCustomData);
  const [insuranceData, setInsuranceData] = useState({});
  const [conventions, setConventions] = useState([]);

  const isNew = useMemo(() => !quotationId, [quotationId]);

  const { data: insurersData } = useQueryWithSimpleErrorHandling(INSURERS_QUERY, { variables: { insuranceTypeId } });
  const insurers = insurersData && insurersData.insurers;

  useQueryWithSimpleErrorHandling(INSURANCE_CONVENTIONS, {
    variables: { insurerId: cascoCustomDataLens.insurerId |> get, insuranceTypeId },
    onCompleted: data => {
      setConventions(data?.insuranceConventions);
    },
    skip: !(cascoCustomDataLens.insurerId |> get) || !insuranceTypeId,
    notifyOnNetworkStatusChange: true
  });

  const { dictionary: risks } = useDictionaryHandling(INSURANCE_RISKS_QUERY, 'insuranceRisks');

  const { loading: quotationLoading } = useQueryWithSimpleErrorHandling(CASCO_QUOTATION, {
    variables: { quotationId: quotationId },
    onCompleted: data => {
      resetCascoCustomDataLens(data?.cascoQuotation);
    },
    skip: !quotationId,
    notifyOnNetworkStatusChange: true
  });

  useQueryWithSimpleErrorHandling(INSURANCE_DATA_QUERY, {
    variables: { documentId },
    skip: !documentId,
    onCompleted: data => {
      setInsuranceData(data?.insuranceData);
    }
  });

  useEffect(() => {
    if (!isNew) return;
    resetCascoCustomDataLens(prevState => ({
      ...prevState,
      insuredAmount: prevState.insuredAmount || insuranceData?.insuredAmount,
      monthNo: prevState.monthNo || insuranceData?.monthNo
    }));
  }, [isNew, insuranceData, resetCascoCustomDataLens]);

  useEffect(() => {
    validate(cascoCustomDataLens |> get, cascoCustomDirtyInfo);
  }, [cascoCustomDirtyInfo, cascoCustomDataLens, validate]);

  useEffect(() => {
    const isCascoCustomDataDirty = isDirty(cascoCustomDirtyInfo);
    setSaveDisabled(!isCascoCustomDataDirty);
  }, [cascoCustomDirtyInfo]);

  useConditionalSubscription(CASCO_CUSTOM_QUOTATION_SAVED, {
    onData: _ => {
      setSaving(false);
      setSaveDisabled(false);
      customQuotationModalOpenVar(false);
    }
  });

  const selectedRisks = useMemo(() => {
    return (cascoCustomDataLens.risks |> sequence)
      .filter(risk => (risk.selected |> get) === true)
      .map(selectedRisk => selectedRisk.riskTypeId |> get);
  }, [cascoCustomDataLens.risks]);

  const [saveCascoQuotation] = useMutation(SAVE_QUOTATION, {
    onError: error => {
      setSaving(false);
      setSaveDisabled(false);
      showError(error);
    }
  });

  const handleSave = useCallback(() => {
    if (!validate(cascoCustomDataLens |> get)) {
      return;
    }

    lastModifiedQuotationVar(cascoCustomDataLens.quotationId |> get);
    setSaving(true);

    saveCascoQuotation({
      variables: {
        input: {
          documentId: documentId,
          quotationId: cascoCustomDataLens.quotationId |> get,
          conventionId: cascoCustomDataLens.conventionId |> get,
          insurerId: cascoCustomDataLens.insurerId |> get,
          period: cascoCustomDataLens.period |> get,
          monthNo: cascoCustomDataLens.monthNo |> get,
          periodicity: cascoCustomDataLens.periodicity |> get,
          suplementPeriod: cascoCustomDataLens.suplementPeriod |> get,
          totalDamageDeductiblePercent: cascoCustomDataLens.totalDamageDeductiblePercent |> get,
          partialDamageDeductibleAmount: cascoCustomDataLens.partialDamageDeductibleAmount |> get,
          discountPercent: cascoCustomDataLens.discountPercent |> get,
          discountAmount: cascoCustomDataLens.discountAmount |> get,
          insuranceAmount: cascoCustomDataLens.insuranceAmount |> get,
          totalInsuranceAmount: cascoCustomDataLens.totalInsuranceAmount |> get,
          insuredAmount: cascoCustomDataLens.insuredAmount |> get,
          risks: (cascoCustomDataLens.risks |> sequence)
            .filter(risk => (risk.selected |> get) === true)
            .map(risk => ({
              riskTypeId: risk.riskTypeId |> get,
              selected: risk.selected |> get
            }))
        }
      }
    });
  }, [cascoCustomDataLens, documentId, saveCascoQuotation, validate]);

  const handleInsurerChange = useCallback(
    value => {
      (cascoCustomDataLens.insurerId |> set)(value);
      (cascoCustomDataLens.conventionId |> set)(null);
    },
    [cascoCustomDataLens]
  );

  const handleRiskSelectionChange = useCallback(
    selectedRisksIds => {
      const cascoCustomSelectedRisks = risks
        .filter(x => selectedRisksIds.includes(x.id))
        .map(x => ({ riskTypeId: x.id, selected: selectedRisksIds.includes(x.id) }));

      (cascoCustomDataLens.risks |> set)(cascoCustomSelectedRisks);
    },
    [cascoCustomDataLens.risks, risks]
  );

  const handleDeleteRiskSelection = useCallback(
    id => {
      (cascoCustomDataLens.risks |> sequence).forEach(risk => (risk.riskTypeId |> get) === id && (risk.selected |> set)(false));
    },
    [cascoCustomDataLens.risks]
  );

  const handleAmountChange = useCallback(
    insuranceAmount => {
      setSaveDisabled(false);
      (cascoCustomDataLens.insuranceAmount |> set)(insuranceAmount);
      (cascoCustomDataLens.period |> get) &&
        (cascoCustomDataLens.totalInsuranceAmount |> set)((insuranceAmount / 12) * (cascoCustomDataLens.period |> get));
    },
    [cascoCustomDataLens.insuranceAmount, cascoCustomDataLens.period, cascoCustomDataLens.totalInsuranceAmount]
  );

  const handlePeriodInfoChange = useCallback(
    (value, prop) => {
      switch (prop) {
        case 'period': {
          (cascoCustomDataLens.period |> set)(value);
          (cascoCustomDataLens.monthNo |> set)(Math.floor(value / (cascoCustomDataLens.periodicity |> get)));
          (cascoCustomDataLens.suplementPeriod |> set)(value % (cascoCustomDataLens.periodicity |> get));
          const insuranceAmount = cascoCustomDataLens.insuranceAmount |> get;
          insuranceAmount && (cascoCustomDataLens.totalInsuranceAmount |> set)((insuranceAmount / 12) * value);
          break;
        }
        case 'periodicity': {
          (cascoCustomDataLens.periodicity |> set)(value);
          (cascoCustomDataLens.monthNo |> set)(Math.floor((cascoCustomDataLens.period |> get) / value));
          (cascoCustomDataLens.suplementPeriod |> set)((cascoCustomDataLens.period |> get) % value);
          break;
        }
        default:
          break;
      }
    },
    [
      cascoCustomDataLens.insuranceAmount,
      cascoCustomDataLens.monthNo,
      cascoCustomDataLens.period,
      cascoCustomDataLens.periodicity,
      cascoCustomDataLens.suplementPeriod,
      cascoCustomDataLens.totalInsuranceAmount
    ]
  );

  return (
    <CASCOCustomQuotationModalComponent
      isNew={isNew}
      cascoCustomDataLens={cascoCustomDataLens}
      insurers={insurers}
      conventions={conventions}
      risks={risks}
      selectedRisks={selectedRisks}
      currencyCode={insuranceData?.currencyCode}
      onInsurerChange={handleInsurerChange}
      onRiskSelectionChange={handleRiskSelectionChange}
      onDeleteSelectedRisk={handleDeleteRiskSelection}
      onSave={handleSave}
      onAmountChange={handleAmountChange}
      onPeriodInfoChange={handlePeriodInfoChange}
      validation={validation}
      saveDisabled={saveDisabled}
      saving={saving}
      quotationLoading={quotationLoading}
    />
  );
};

CASCOCustomQuotationContainer.propTypes = {
  isNew: PropTypes.bool,
  insuranceTypeId: PropTypes.number,
  documentId: PropTypes.number,
  quotationId: PropTypes.number
};

export default CASCOCustomQuotationContainer;
